/**
 * ============================================================================
 * CrossRefsList — Phase 6e frontend (Equipment & Validation).
 * Owner: Claude 3 (Frontend & UX).
 * Purpose: list documents that have been quarantine-flagged because of this
 *          equipment's RESTRICTED state (e.g. batch records that referenced
 *          the equipment during the restricted window). Empty by default when
 *          the equipment is healthy.
 *
 * A11Y: each row is a button with an aria-label that includes the document
 *  number and the quarantine timestamp so the action is unambiguous out of
 *  context.
 * ============================================================================ */

import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './EquipmentDetail.module.css';
import type { CrossRefDoc } from './useEquipmentDetailQuery';

export interface CrossRefsListProps {
  rows: CrossRefDoc[];
}

export function CrossRefsList({ rows }: CrossRefsListProps) {
  const { t, i18n } = useTranslation();
  const dateFmt = useMemo(
    () =>
      new Intl.DateTimeFormat(i18n.language, {
        dateStyle: 'medium',
        timeStyle: 'short',
      }),
    [i18n.language],
  );

  if (rows.length === 0) {
    return (
      <p className={styles.tabEmpty}>
        {t('equipment.detail.crossRefs.empty', {
          defaultValue:
            'No documents are currently quarantined because of this equipment.',
        })}
      </p>
    );
  }

  return (
    <ul
      className={styles.crossRefList}
      aria-label={t('equipment.detail.crossRefs.listAria', {
        defaultValue: 'Quarantined documents linked to this equipment',
      })}
    >
      {rows.map((d) => (
        <li key={d.document_id} className={styles.crossRefItem}>
          <code className={styles.codeChip}>{d.document_number}</code>
          <span>{d.title}</span>
          <time className={styles.subdued} dateTime={d.quarantined_at_utc}>
            {t('equipment.detail.crossRefs.quarantinedAt', {
              defaultValue: 'Quarantined {{ts}}',
              ts: dateFmt.format(new Date(d.quarantined_at_utc)),
            })}
          </time>
          <span className={`${styles.chip} ${styles.lifeRestricted}`}>
            {d.state}
          </span>
        </li>
      ))}
    </ul>
  );
}
